import React, { useEffect, useState } from "react";
import { Link as RouterLink, withRouter, Redirect } from "react-router-dom";
import * as Yup from "yup";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { isEmpty, has } from "lodash";
import { loginUser, loginWithGoogle } from "../_actions/authActions";
import {
  ROOT,
  REGISTER_PATH,
  FORGOT_PASSWORD_PATH,
  LIFETIME,
} from "../_utils/routes";
import FormErrors from "./FormErrors";
import { GOOGLE_CLIENT_ID } from "../_actions/endpoints";
import TldrBase from "./TldrBase";
import { setErrorMessage } from "./statelessView";
import { getSubscription } from "../_actions/subscriptionActions";
import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Link,
  Text,
} from "@chakra-ui/react";
import { FaArrowCircleRight } from "react-icons/fa";
import { useTranslation, withTranslation } from "react-i18next";
import { HTTP_400_ERROR } from "../_actions/types";
import { Field, Form, Formik } from "formik";
import GoogleLogin from "react-google-login";
import {POST_LOGIN_STORAGE_KEY} from "@simplified-workspace/shared/util";
import useGoogleApiLoader from "../_hooks/useGoogleApiLoader";

const Login = (props) => {

  const loadGapi = useGoogleApiLoader();
  useEffect(() => {
    loadGapi("client:auth2")
  }, [])

  const { t } = useTranslation(["auth"]);
  const [loginWithEmailLoading, setLoginWithEmailLoading] = useState(false);
  const [googleLoginErrors, setGoogleLoginErrors] = useState("");

  const loginValidationSchema = Yup.object().shape({
    email: Yup.string("").email(t("login.email_error")),
    password: Yup.string().min(8, t("login.password_error")),
  });

  const onFailure = (error) => {
    let errorMessage = setErrorMessage(error);
    setGoogleLoginErrors(errorMessage);
  };

  const googleResponse = (response, e) => {
    const tokenJSON = { access_token: response.accessToken };
    props.loginWithGoogle(tokenJSON, { ...props }).then(() => {
      props.getSubscription();
    });
  };

  function onSubmit(values) {
    setLoginWithEmailLoading(true);

    const { from } = props.location.state || { from: { pathname: "/" } };
    const userData = {
      email: values.email,
      password: values.password,
    };
    if (from && from.location === LIFETIME) {
      props.history.replace({ pathname: LIFETIME });
    } else {
      props
        .loginUser(userData, { ...props })
        .then((res) => {
          setLoginWithEmailLoading(false);
          if (!res || res.type !== HTTP_400_ERROR) {
            props.getSubscription();
          }
        })
        .catch((error) => {
          setLoginWithEmailLoading(false);
        });
    }
  }

  const nonFieldErrors = has(props.errors, "non_field_errors") && (
    <Box status="error" className="alert alert-danger">
      <FormErrors formErrors={props.errors} />
    </Box>
  );

  const loginErrors = !isEmpty(googleLoginErrors) && (
    <Box status="error" className="alert alert-danger">
      <FormErrors formErrors={googleLoginErrors} />
    </Box>
  );

  const { from } = props.location.state || { from: { pathname: "/" } };
  if (props.auth.isAuthenticated) {
    if (localStorage.getItem("path")) {
      return <Redirect to={localStorage.getItem("path")} />;
    }
    if (localStorage.getItem(POST_LOGIN_STORAGE_KEY)) {
      const postLogin = JSON.parse(localStorage.getItem(POST_LOGIN_STORAGE_KEY))
        props.history.push(postLogin.path);
      return ;
    }
    return <Redirect to={from ? from : ROOT} />;
  }

  return (
    <TldrBase
      heading={t("login.welcome")}
      showBackgroundImage
      wrapperClassName="login-wrapper"
    >
      <GoogleLogin
        buttonText={t("login.google")}
        clientId={GOOGLE_CLIENT_ID}
        onSuccess={googleResponse}
        onFailure={onFailure}
        cookiePolicy={"single_host_origin"}
        className="social-btn-google"
        theme="dark"
      />

      <Flex align="center">
        <Divider mr={2} mt={4} mb={4} />
        <Text>
          {t("login.or")}
        </Text>
        <Divider ml={2} mt={4} mb={4} />
      </Flex>
      {loginErrors}

      {nonFieldErrors}
      <Formik
        onSubmit={onSubmit}
        initialValues={{ email: "", password: "" }}
        validationSchema={loginValidationSchema}
      >
        {({ errors, values }) => (
          <Form>
            <Field name="email">
              {({ field, form }) => (
                <FormControl mb={3} isInvalid={form.errors.email}>
                  <Input
                    name="email"
                    fontSize={"inherit"}
                    size={"lg"}
                    type="email"
                    placeholder={t("login.email")}
                    aria-label="Email"
                    {...field}
                  />
                  <FormErrorMessage>{form.errors.email}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Field name="password">
              {({ field, form }) => (
                <FormControl isInvalid={form.errors.password}>
                  <Input
                    name="password"
                    fontSize={"inherit"}
                    size={"lg"}
                    type="password"
                    placeholder={t("login.password")}
                    aria-label="Password"
                    {...field}
                  />
                  <FormErrorMessage>{form.errors.password}</FormErrorMessage>
                </FormControl>
              )}
            </Field>
            <Flex mt={1} mb={3} justify="flex-end">
              <Link as={RouterLink} to={FORGOT_PASSWORD_PATH}>
                {t("login.forgot")}
              </Link>
            </Flex>
            <Box mb={3}>
              <Button
                disabled={
                  !isEmpty(errors) ||
                  values.email === "" ||
                  values.password === ""
                }
                variant="solid"
                isLoading={loginWithEmailLoading}
                type="submit"
                size="lg"
                isFullWidth
                loadingText=""
              >
                {!loginWithEmailLoading && from?.pathname === LIFETIME
                  ? t("login.verify")
                  : !loginWithEmailLoading && (
                      <>
                        {t("login.go")}&nbsp;
                        <FaArrowCircleRight />
                      </>
                    )}
              </Button>
            </Box>
          </Form>
        )}
      </Formik>

      <Flex mt={7} justify="flex-end">
        <label>
          {t("login.not_on_simplified")}{" "}
          <Link as={RouterLink} to={REGISTER_PATH}>
            {t("login.signup")}
          </Link>
        </label>
      </Flex>
    </TldrBase>
  );
};

Login.propTypes = {
  loginUser: PropTypes.func.isRequired,
  auth: PropTypes.object.isRequired,
  errors: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  errors: state.errors,
  foldersPage: state.folders.foldersPage,
});

export default connect(mapStateToProps, {
  loginUser,
  loginWithGoogle,
  getSubscription,
})(withRouter(withTranslation(["auth"])(Login)));
